import { notification } from 'antd'
import i18next from 'i18next'
import { action, computed, makeObservable, observable } from 'mobx'

import { CountryItem } from 'components/CountryPhoneInput/CountryPhoneInput'
import { ALL_EXCEPT_NUMBERS_REGEXP, NOT_LATIN_CYRILLIC } from 'shared/constants/regExp'
import { BaseOption, Country } from 'shared/types/types'
import { PropType } from 'types/types'

import { userApi } from '../../api'
import {
  DepartmentCodeFormat,
  documentNumberFormat,
  passport as passportRequisites,
  RequisitesCIS,
} from '../../components/PersonalDataSteps/PassportStep/PassportForm/requisites'
import {
  accountDetails,
  citizenshipOptions,
  companyNameWithCompanyType,
  countries,
  countryInnValue,
  DaDataOrderLoadProp,
  taxCheckStatusName,
} from '../../data'
import {
  Citizenship,
  CompanyInfoDTO,
  CompanyType,
  DocumentType,
  GENDER,
  TaxCheckStatusType,
  TaxTypes,
  UrlType,
  UserModuleKeys,
} from '../../types'
import { regExpOnlyLatin } from '../../utils/getRequiredError'
import { validateAnBik } from '../../utils/validateAnBik'

const getDataKey = (key: string) => `${UserModuleKeys.data}.${key}`

export class CompanyDetailsViewModel {
  @observable loading = false

  // шаг 1. Форма деятельности
  @observable selectedCountry: CountryItem = countries[0].value
  @observable companyType: CompanyType = CompanyType.ip
  @observable taxType: TaxTypes = TaxTypes.usn
  @observable taxFiles: string[] = []
  @observable taxFilesUI: string[] = []
  @observable inn = ''
  @observable innText = ''
  @observable companyName = ''
  @observable bankInn = ''
  @observable corAccount = ''
  @observable bankName = ''
  @observable bik = ''
  @observable payAccount = ''

  @observable taxCheckStatus: TaxCheckStatusType = TaxCheckStatusType.review
  @observable taxCheckStatusComment = ''

  @observable legalAddress = ''
  @observable gln = ''

  @observable loadingCompanyInfo = false
  @observable loadedCompanyInfo = false
  @observable needRemoteValidateBik = true

  @observable acceptPersonalInfo: boolean = false

  // шаг 2. Персональные данные
  @observable selectedCitizenship: BaseOption<Citizenship> = citizenshipOptions[0]
  @observable documentType: DocumentType = DocumentType.passport
  @observable passport = ''
  @observable identificationNumber = ''
  @observable dateOfIssue = ''
  @observable departmentCode = ''
  @observable gender: GENDER = GENDER.MALE // МУЖ. ЖЕН.
  @observable dateOfBirthday = ''
  @observable placeOfBirth = ''
  @observable passportIssued = ''
  @observable nationality = ''
  @observable expirationDate = ''
  @observable documentNumber = ''
  @observable iin = ''
  @observable socialCardNumber = ''
  @observable personalNumber = ''
  @observable registrationAddress = ''
  @observable permanentResidenceAddress = ''
  @observable innCitizen = ''

  // шаг 3. Прикрепление файлов удостоверения личности
  @observable passportFileLoading: boolean = false

  @observable documentPhotoUI: string[] = []
  @observable withDocumentPhotoUI: string[] = []

  @observable documentPhoto: string[] = []
  @observable withDocumentPhoto: string[] = []

  @observable dadataMap = {
    [DaDataOrderLoadProp.bik]: false,
    [DaDataOrderLoadProp.companyName]: false,
  }

  constructor() {
    makeObservable(this)
  }

  @computed
  get isInvalidLegalAddress(): boolean {
    return this.legalAddress.trim().length === 0
  }

  @computed
  get isInvalidGln(): boolean {
    return this.gln.trim().length !== accountDetails.BY.gln
  }

  @computed
  get isInvalidInnCitizen(): boolean {
    return this.innCitizen.trim().length < 9
  }

  @computed
  get isInvalidRegistrationAddress(): boolean {
    return this.registrationAddress.trim().length < 2
  }

  @computed
  get isInvalidPermanentResidenceAddress(): boolean {
    return this.permanentResidenceAddress.trim().length < 2
  }

  @computed
  get isInvalidIdentificationNumber(): boolean {
    if (this.selectedCitizenship.value !== Citizenship.RU) {
      return (
        this.identificationNumber.trim().length !==
        RequisitesCIS[this.selectedCitizenship.value][DocumentType.identification].maxLength
      )
    }

    return this.identificationNumber.trim() === ''
  }

  @computed
  get isInvalidDocumentPhoto(): boolean {
    return !this.documentPhoto[0] || !this.documentPhoto[1]
  }

  @computed
  get isInvalidWithDocumentPhoto(): boolean {
    return this.documentType === DocumentType.passport
      ? !this.withDocumentPhoto[0]
      : !(this.withDocumentPhoto[0] && this.withDocumentPhoto[1])
  }

  @computed
  get isInvalidPersonalNumber(): boolean {
    return this.personalNumber.trim().length < 14
  }

  @computed
  get isInvalidSocialCardNumber(): boolean {
    return this.socialCardNumber.trim().length < 10
  }

  @computed
  get isInvalidIin(): boolean {
    return this.iin.trim().length !== 12
  }

  @computed
  get isInvalidDocumentNumber(): boolean {
    return this.documentNumber.trim().length !== documentNumberFormat[this.selectedCitizenship.value].maxLength
  }

  @computed
  get isInvalidNationality(): boolean {
    return this.nationality.trim().length < 2
  }

  @computed
  get onlyTaxUsnCountry(): boolean {
    return [Country.BY, Country.KZ, Country.AM, Country.KG, Country.UZ].some(
      (country) => country === this.selectedCountry.country,
    )
  }

  @computed
  get hasTaxFiles(): boolean {
    return this.taxFiles.length !== 0
  }

  @computed
  get isInvalidPassport(): boolean {
    return this.passport.trim().length !== passportRequisites[this.selectedCitizenship.value].maxLength
  }

  @computed
  get isInvalidDateOfIssue(): boolean {
    return this.dateOfIssue === ''
  }

  @computed
  get isInvalidDepartmentCode(): boolean {
    const maxLenght = DepartmentCodeFormat[this.selectedCitizenship.value][this.documentType]?.maxLength
    return maxLenght ? this.departmentCode.trim().length !== maxLenght : this.departmentCode.trim() === ''
  }

  @computed
  get isInvalidGender(): boolean {
    return !this.gender
  }

  @computed
  get isInvalidDateOfBirthday(): boolean {
    return this.dateOfBirthday === ''
  }

  @computed
  get isInvalidExpirationDate(): boolean {
    return this.expirationDate === ''
  }

  @computed
  get isInvalidPlaceOfBirth(): boolean {
    return this.placeOfBirth.trim().length === 0
  }

  @computed
  get isInvalidPassportIssued(): boolean {
    return this.passportIssued.trim().length === 0
  }

  @computed
  get companyTypeName() {
    const nameWithType = companyNameWithCompanyType[this.selectedCountry.country]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const result = nameWithType.find(([_, type]) => type === this.companyType)?.[0] ?? ''
    return result as string
  }

  @computed
  get isTaxCheckStatusSuccess(): boolean {
    return this.taxCheckStatus === TaxCheckStatusType.success
  }

  @computed
  get taxCheckStatusName(): string {
    return taxCheckStatusName[this.taxCheckStatus]
  }

  @computed
  get isValidInn() {
    const result = this.inn && this.inn?.trim()?.length > 0

    if (this.selectedCountry.country === Country.GE) {
      return result
    }

    return result && (this.inn.length === this.innMinLength || this.inn.length === this.innMaxLength)
  }

  @computed
  get innLable() {
    if (this.selectedCountry.country === Country.UZ) {
      return i18next.t(getDataKey(countryInnValue[Country.UZ][this.companyType]))
    }
    return i18next.t(getDataKey(countryInnValue[this.selectedCountry.country]))
  }

  @computed
  get isCorAccountInvalid() {
    return this.corAccount.length !== this.corAccountMaxLength
  }

  @computed
  get isOnlyLatinCompanyName() {
    return this.selectedCountry.country === Country.AM && regExpOnlyLatin(this.companyName)
  }

  @computed
  get isOnlyLatinBankName() {
    return this.selectedCountry.country === Country.AM && regExpOnlyLatin(this.bankName)
  }

  @computed
  get corAccountMaxLength() {
    return accountDetails[Country.RU].corAccount
  }

  @computed
  get payAccountMaxLength() {
    const payAccountExtremum = accountDetails[this.selectedCountry.country].payAccount

    if (typeof payAccountExtremum === 'number') {
      return payAccountExtremum
    }

    return Math.max(...payAccountExtremum)
  }

  @computed
  get payAccountMinLength() {
    const payAccountExtremum = accountDetails[this.selectedCountry.country].payAccount

    if (typeof payAccountExtremum === 'number') {
      return payAccountExtremum
    }

    return Math.min(...payAccountExtremum)
  }

  @computed
  get bikMaxLength() {
    const tmpBik = accountDetails[this.selectedCountry.country].bik

    if (typeof tmpBik === 'number') {
      return tmpBik
    }

    return Math.max(...tmpBik)
  }

  @computed
  get bikMinLength() {
    const tmpBik = accountDetails[this.selectedCountry.country].bik

    if (typeof tmpBik === 'number') {
      return tmpBik
    }

    return Math.min(...tmpBik)
  }

  @computed
  get innMaxLength() {
    if (this.selectedCountry.country === Country.RU) {
      return accountDetails[this.selectedCountry.country].inn[this.companyType]
    }

    if (this.selectedCountry.country === Country.UZ) {
      return Math.max(...accountDetails[Country.UZ].inn)
    }

    return accountDetails[this.selectedCountry.country].inn
  }

  get innMinLength() {
    if (this.selectedCountry.country === Country.RU) {
      return accountDetails[this.selectedCountry.country].inn[this.companyType]
    }

    if (this.selectedCountry.country === Country.UZ) {
      return Math.min(...accountDetails[Country.UZ].inn)
    }

    return accountDetails[this.selectedCountry.country].inn
  }

  @computed
  get isAnBikInvalid() {
    if (this.isCIS) {
      return false
    }
    return !validateAnBik(this.payAccount, this.bik)
  }

  @computed
  get isBankNameInvalid() {
    return this.bankName?.trim().length === 0
  }

  /** Название компании или организации */
  @computed
  get isCompanyNameInvalid() {
    if (!this.companyName) {
      return true
    }
    return this.companyName.trim().length === 0
  }

  /** БИК для всех стран */
  @computed
  get isBikInvalid() {
    const bik = accountDetails[this.selectedCountry.country].bik
    if (typeof bik === 'number') {
      return this.bik.trim()?.length !== bik
    }

    return !bik.some((value) => value === this.bik.trim()?.length)
  }

  /** ИНН всех стран */
  @computed
  get isInnInvalid() {
    return !this.isValidInn || this.innText?.length < this.innMaxLength
  }

  /** Расчетный счет или Р/С контрагента */
  @computed
  get isPayAccountInvalid() {
    if (this.selectedCountry.country === Country.AM) {
      const payAccountExtremum = accountDetails[Country.AM].payAccount
      return Math.min(...payAccountExtremum) > this.payAccount.trim()?.length
    }

    return this.payAccount.trim()?.length < this.payAccountMaxLength
  }

  @computed
  get isPayAccountEmpty() {
    return this.payAccount?.trim().length === 0
  }

  @computed
  get isTaxOSNInvalid() {
    return this.taxType === TaxTypes.osn && this.taxFiles.length === 0
  }

  /** СНГ */
  @computed
  get isCIS() {
    return this.selectedCountry.country !== Country.RU
  }

  @action
  setAcceptPersonalInfo = (value?: boolean) => {
    this.acceptPersonalInfo = value ?? !this.acceptPersonalInfo
  }

  @action
  setLegalAddress = (value: string) => {
    this.legalAddress = value
  }

  @action
  setGln = (value: string) => {
    this.gln = value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
  }

  @action
  setInnCitizen = (value: string) => {
    if (this.selectedCitizenship.value === Citizenship.TJ) {
      this.innCitizen = value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    } else {
      this.innCitizen = value
    }
  }

  @action
  setRegistrationAddress = (value: string) => {
    this.registrationAddress = value
  }

  @action
  setPermanentResidenceAddress = (value: string) => {
    this.permanentResidenceAddress = value
  }

  @action
  setPersonalNumber = (value: string) => {
    if (this.selectedCitizenship.value === Citizenship.UZ || this.selectedCitizenship.value === Citizenship.KG) {
      this.personalNumber = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    } else {
      this.personalNumber = value
    }
  }

  @action
  setSocialCardNumber = (value: string) => {
    this.socialCardNumber = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
  }

  @action
  setIin = (value: string) => {
    this.iin = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
  }

  @action
  setDocumentNumber = (value: string) => {
    if (this.selectedCitizenship.value === Citizenship.KZ || this.selectedCitizenship.value === Citizenship.AM) {
      this.documentNumber = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    } else {
      this.documentNumber = value
    }
  }

  @action
  setNationality = (value: string) => {
    this.nationality = value.replace(NOT_LATIN_CYRILLIC, '')
  }

  @action
  setGender = (value: GENDER) => {
    this.gender = value
  }

  @action
  setPlaceOfBirth = (value: string) => {
    this.placeOfBirth = value
  }

  @action
  setPassportIssued = (value: string) => {
    this.passportIssued = value
  }

  @action
  setTaxType = (value: TaxTypes) => {
    this.taxType = value
  }

  @action
  setCompanyType = (value: CompanyType) => {
    this.companyType = value
  }

  @action
  setBankInn = (value: string) => {
    this.bankInn = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
  }

  @action
  setCorAccount = (value: string) => {
    this.corAccount = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
  }

  @action
  setSelectedCountry = (value: CountryItem) => {
    this.selectedCountry = value
  }

  @action
  setSelectedCitizenship = (value: any) => {
    this.selectedCitizenship = value
  }

  @action
  setDocumentType = (value: DocumentType) => {
    this.documentType = value
  }

  @action
  setCompanyName = (value: string) => {
    this.companyName = value
  }

  @action
  setInn = (value: string) => {
    this.inn = value
  }

  @action
  setInnText = (value: string) => {
    this.innText = value
  }

  @action
  setTaxCheckStatus = (value: TaxCheckStatusType) => {
    this.taxCheckStatus = value
  }

  @action
  setTaxCheckStatusComment = (value: string) => {
    this.taxCheckStatusComment = value
  }

  @action
  setBik = (value: string) => {
    if (
      this.selectedCountry.country === Country.TJ ||
      this.selectedCountry.country === Country.KG ||
      this.selectedCountry.country === Country.UZ ||
      this.selectedCountry.country === Country.RU
    ) {
      this.bik = value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    } else {
      this.bik = value
    }
    this.needRemoteValidateBik = true
  }

  @action
  setNeedRemoteValidateBik = (value: boolean) => {
    this.needRemoteValidateBik = value
  }

  setPayAccountPrefix = (value: string) => {
    let payAccount = ''
    value = value || ''

    const setPrefix = (countryCode: string) => {
      if ((value.startsWith(countryCode[0]) && value.length < 2) || value.startsWith(countryCode)) {
        payAccount = value
      } else {
        payAccount = `${countryCode + value}`
      }
    }

    if (
      this.selectedCountry.country === Country.KZ ||
      this.selectedCountry.country === Country.BY ||
      this.selectedCountry.country === Country.GE
    ) {
      setPrefix(this.selectedCountry.country)
    } else {
      payAccount = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    }

    return payAccount
  }

  @action
  setLoadedCompanyInfo = (value: boolean) => {
    this.loadedCompanyInfo = value
  }

  @action
  setPayAccount = (value: string) => {
    if (
      this.selectedCountry.country === Country.AM ||
      this.selectedCountry.country === Country.TJ ||
      this.selectedCountry.country === Country.RU ||
      this.selectedCountry.country === Country.KG ||
      this.selectedCountry.country === Country.UZ
    ) {
      this.payAccount = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    } else {
      this.payAccount = value
    }
  }

  @action
  setBankName = (value: string) => {
    this.bankName = value
  }

  @action
  setLoadingCompanyInfo = (value: boolean) => {
    this.loadingCompanyInfo = value
  }

  @action
  setPassport = (value: string) => {
    if (this.selectedCitizenship.value === Citizenship.RU) {
      this.passport = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    } else {
      this.passport = value
    }
  }

  @action
  setIdentificationNumber = (value: string) => {
    if (
      this.selectedCitizenship.value === Citizenship.KZ ||
      this.selectedCitizenship.value === Citizenship.KG ||
      this.selectedCitizenship.value === Citizenship.TJ ||
      this.selectedCitizenship.value === Citizenship.GE
    ) {
      this.identificationNumber = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    } else {
      this.identificationNumber = value
    }
  }

  @action
  setIdentificationNumberOld = (value: string) => {
    this.identificationNumber = value
  }

  @action
  setDateOfBirthday = (value: string) => {
    this.dateOfBirthday = value
  }

  @action
  setExpirationDate = (value: string) => {
    this.expirationDate = value
  }

  @action
  setDateOfIssue = (value: string) => {
    this.dateOfIssue = value
  }

  @action
  setDepartmentCode = (value: string) => {
    if (this.selectedCitizenship.value === Citizenship.RU) {
      const digits = value.replace(/\D/g, '')

      if (digits.length === 6) {
        this.departmentCode = digits.slice(0, 3) + '-' + digits.slice(3, 6)
      } else {
        this.departmentCode = digits
      }
    } else if (this.selectedCitizenship.value === Citizenship.KG || this.selectedCitizenship.value === Citizenship.UZ) {
      this.departmentCode = value
    } else {
      this.departmentCode = value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    }
  }

  @action
  setPassportFileLoading = (value: boolean) => {
    this.passportFileLoading = value
  }

  @action
  setTaxFiles = (value: string[]) => {
    this.taxFiles = value
    this.taxFilesUI = value
  }

  @action
  setTaxFilesUI = (value: string[]) => {
    this.taxFilesUI = value
  }

  @action
  setLoading = (value: boolean) => {
    this.loading = value
  }

  @action
  updateFields = (text: string) => {
    this.setLoadedCompanyInfo(false)
    if (text.length === this.innMaxLength) {
      this.loadDData(text)
      this.setInn(text)
    } else {
      this.clearCompanyInfo()
    }
  }

  @action
  isValidBankInn = async () => {
    try {
      if (this.loading) {
        return
      }
      this.setLoading(true)
      const innResponse = await userApi.getCompanyInfo(this.bankInn, UrlType.company)
      if (innResponse?.suggestions) {
        for (const { data } of innResponse.suggestions || []) {
          if (data.state.status === 'ACTIVE') {
            return true
          }
        }
      }
      return false
    } catch (error) {
      return false
    } finally {
      this.setLoading(false)
    }
  }

  @action
  isValidBik = async () => {
    try {
      if (this.loading) {
        return
      }
      this.setLoading(true)
      const bikResponse = await userApi.getCompanyInfo(this.bik, UrlType.bank)

      if (bikResponse?.suggestions) {
        for (const { data } of bikResponse.suggestions || []) {
          if (data.state.status === 'ACTIVE') {
            return true
          }
        }
      }
      return false
    } catch (error) {
      this.setNeedRemoteValidateBik(false)
      return true
    } finally {
      this.setLoading(false)
    }
  }

  @action
  loadDData = async (inn: string) => {
    this.setLoadingCompanyInfo(true)

    try {
      const innResponse = await userApi.getCompanyInfo(inn, UrlType.company)

      if (innResponse?.suggestions) {
        this.handleCompanyInfo(innResponse.suggestions)
      } else {
        this.clearStates()
        this.setLoadedCompanyInfo(false)
      }
      this.setLoadingCompanyInfo(false)
    } catch (error) {
      this.setLoadedCompanyInfo(false)
      this.setLoadingCompanyInfo(false)
    }
  }

  convertPayAccountValue = (payAccountWithPrefix: string) => {
    let convertedPayAccount = payAccountWithPrefix

    if (
      this.selectedCountry.country === Country.KZ ||
      this.selectedCountry.country === Country.BY ||
      this.selectedCountry.country === Country.GE
    ) {
      const validRegexp = /([A-Za-z0-9]+)/gm
      convertedPayAccount = payAccountWithPrefix
        ?.split(validRegexp)
        .filter((x) => validRegexp.test(x))
        .join('')
    }

    return convertedPayAccount
  }

  @action
  clearActivityStep = () => {
    this.setCompanyType(CompanyType.ip)
    this.setBankInn('')
    this.setInnText('')
    this.setInn('')
    this.setIin('')
    this.dadataMap = {
      [DaDataOrderLoadProp.bik]: false,
      [DaDataOrderLoadProp.companyName]: false,
    }
    this.setCompanyName('')
    this.setBankName('')
    this.setBik('')
    this.setPayAccount('')
    this.setCorAccount('')
    this.setAcceptPersonalInfo(false)
    this.setTaxFiles([])
    this.setTaxType(TaxTypes.usn)
    this.setLegalAddress('')
    this.setGln('')

    this.setTaxCheckStatus(TaxCheckStatusType.review)
    this.setTaxCheckStatusComment('')
  }

  @action
  clearPassportStep = () => {
    this.setPassport('')
    this.setDateOfIssue('')
    this.setDepartmentCode('')
    this.setGender(GENDER.MALE)
    this.setDateOfBirthday('')
    this.setExpirationDate('')
    this.setPlaceOfBirth('')
    this.setPassportIssued('')
    this.setNationality('')
    this.setDocumentNumber('')
    this.setIin('')
    this.setSocialCardNumber('')
    this.setPersonalNumber('')
    this.setRegistrationAddress('')
    this.setPermanentResidenceAddress('')
    this.setInnCitizen('')
    this.setIdentificationNumber('')
  }

  @action
  clearCompanyInfo = (clearFile: boolean = true) => {
    this.setInnText('')
    this.setInn('')
    this.setBik('')
    this.setPayAccount('')
    this.setCompanyName('')
    this.setCorAccount('')
    this.setBankInn('')
    this.setBankName('')
    if (clearFile) {
      this.setTaxType(TaxTypes.usn)
      this.setTaxFiles([])
    }
    this.dadataMap = {
      [DaDataOrderLoadProp.bik]: false,
      [DaDataOrderLoadProp.companyName]: false,
    }
    this.setPassport('')
    this.setDateOfIssue('')
    this.setDepartmentCode('')
    this.setGender(GENDER.MALE)
    this.setDateOfBirthday('')
    this.setExpirationDate('')
    this.setPlaceOfBirth('')
    this.setPassportIssued('')
    this.setNationality('')
    this.setDocumentNumber('')
    this.setIin('')
    this.setSocialCardNumber('')
    this.setPersonalNumber('')
    this.setRegistrationAddress('')
    this.setPermanentResidenceAddress('')
    this.setInnCitizen('')
    this.setIdentificationNumber('')
    this.setDocumentType(DocumentType.passport)
    this.setSelectedCitizenship(citizenshipOptions[0])
    this.documentPhotoUI = []
    this.withDocumentPhotoUI = []
    this.documentPhoto = []
    this.withDocumentPhoto = []

    this.setTaxCheckStatus(TaxCheckStatusType.review)
    this.setTaxCheckStatusComment('')

    this.setLegalAddress('')
    this.setGln('')
  }

  private clearStates() {
    this.clearCompanyInfo(false)
    notification.error({
      message: 'Ошибка',
      description: 'ИНН не найден',
    })
  }

  @action
  private handleCompanyInfo = (suggestions: PropType<CompanyInfoDTO, 'suggestions'>) => {
    if (!suggestions) {
      return
    }

    // find active suggestion
    let activeIndex = -1
    for (const { data } of suggestions) {
      // TODO: почему +1?
      if (data.state.status === 'ACTIVE') {
        activeIndex += 1
      }
    }
    if (activeIndex === -1) {
      this.clearStates()
      this.setLoadedCompanyInfo(false)
      return
    }

    const { data, value } = suggestions[activeIndex]
    this.withDadata(this.setCompanyName(value ?? ''), value, DaDataOrderLoadProp.companyName)
    if (data) {
      const { bik } = data

      this.withDadata(this.setBik(bik ?? ''), bik, DaDataOrderLoadProp.bik)

      this.setLoadedCompanyInfo(true)
    } else {
      this.clearStates()
      this.setLoadedCompanyInfo(false)
    }
  }

  @action
  private withDadata = (cb: any, value: string | undefined, mapValue: DaDataOrderLoadProp) => {
    if (value) {
      this.dadataMap[mapValue] = true
    }
  }
}
